
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

export type Branding = {
  appName: string;
  appLogoUrl: string | null;
  appIconUrl: string | null;
  feedbackFormUrl: string;
  /** When false, hide responses nav/UI; missing defaults to true. */
  responsesEnabled: boolean;
  /** When true, show the stage tablet queue. Missing defaults to false. */
  stageEnabled: boolean;
};

type BrandingContextValue = {
  branding: Branding;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const DEFAULT_BRANDING: Branding = {
  appName: 'One Voice',
  appLogoUrl: null,
  appIconUrl: null,
  feedbackFormUrl: '',
  responsesEnabled: true,
  stageEnabled: false,
};

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

async function fetchBranding(): Promise<Branding> {
  const { data, error } = await api.get<Branding>('/branding', { auth: false });
  if (error || !data) return DEFAULT_BRANDING;
  return {
    appName: (data.appName || DEFAULT_BRANDING.appName).toString(),
    // API returns a short-lived presigned bucket URL (same pattern as video responses).
    appLogoUrl: data.appLogoUrl ? data.appLogoUrl.toString() : null,
    appIconUrl: data.appIconUrl ? data.appIconUrl.toString() : null,
    feedbackFormUrl: (data.feedbackFormUrl || '').toString(),
    responsesEnabled:
      data.responsesEnabled === undefined || data.responsesEnabled === null
        ? true
        : Boolean(data.responsesEnabled),
    stageEnabled: data.stageEnabled === true,
  };
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = async () => {
    const next = await fetchBranding();
    setBranding(next);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchBranding();
        if (!cancelled) setBranding(next);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({ branding, isLoading, refresh }),
    [branding, isLoading]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}

